import { Button, CircularProgress, Dialog, Grid } from '@material-ui/core';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { Dispatch, Reducer, useReducer, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import en from '../../../lang/en-us';
import { SubmitButton } from '../../../lib/Button';
import { OtpField } from '../../../lib/OtpField';
import TextInput from '../../../lib/TextField';
import ToggleTabs, { OFF, ON } from '../../../lib/ToggleTabs';
import {
    dormantPanCheck,
    registerDormantOtp,
    verifyDormantOtp,
} from '../../../redux-store/actions/link';
import { InactiveFolioLoginFormik, InactiveFolioPaths } from './types';
import { emailRegex, numberRegex, panRegex } from '../../../utils/constants';
import { IsAllow } from '../../../redux-store/types/portfolio';
import { maskEmail, maskMobileNumber } from '../../../utils/utilityFunctions';

const { dashboard } = en;

enum UserState {
    Initial,
    Registered,
    Unregistered,
    Verified,
}

enum UserEvent {
    CheckPan,
    RegisterUser,
    VerifyOTP,
    Reset,
}

const {
    login: { form, headerLinks },
} = en;

const userReducer: Reducer<UserState, { action: UserEvent; loginPanExists?: 'Y' | 'N' }> = (
    state,
    event,
) => {
    switch (state) {
        case UserState.Initial:
            if (event.action === UserEvent.CheckPan) {
                return event.loginPanExists === 'Y' ? UserState.Registered : UserState.Unregistered;
            }
            break;
        case UserState.Unregistered:
            if (event.action === UserEvent.RegisterUser) {
                return UserState.Registered;
            }
            if (event.action === UserEvent.Reset) {
                return UserState.Initial;
            }
            break;
        case UserState.Registered:
            if (event.action === UserEvent.VerifyOTP) {
                return UserState.Verified;
            }
            if (event.action === UserEvent.Reset) {
                return UserState.Initial;
            }
            break;
    }
    return state;
};

const initialValues: InactiveFolioLoginFormik = {
    userId: '',
    registerType: 'email',
    email: '',
    mobile: '',
};

const initState = {
    otp: null,
    reqId: null,
    otpRef: null,
};

const DifferentPAN = ({
    userDispatch,
    reset,
}: {
    userDispatch: Dispatch<{ action: UserEvent; loginPanExists?: 'Y' | 'N' | undefined }>;
    reset: any;
}) => {
    return (
        <p
            style={{
                fontSize: 14,
                color: '#836fe0',
                cursor: 'pointer',
                display: 'inline-block',
                // padding: '10px',
                // marginTop: '-15px',
                marginBottom: '10px',
                textDecoration: 'underline',
            }}
        >
            <a
                className="forgot-password-btn"
                onClick={() => {
                    reset();
                    userDispatch({
                        action: UserEvent.Reset,
                    });
                }}
            >
                Return to the previous page to try again with a different PAN or PEKRN
            </a>
        </p>
    );
};

const NewUserValidationSchema = yup.object({
    userId: yup
        .string()
        .required('Please enter PAN/PEKRN')
        .test('isValidPAN', 'Please enter a valid PAN/PEKRN', (value) =>
            panRegex.test(value || ''),
        ),
    email: yup.string().when('registerType', {
        is: 'email',
        then: yup
            .string()
            .required('Please enter your email')
            .test('isValidEmail', 'Please enter a Valid Email', (value) =>
                emailRegex.test(value || ''),
            ),
    }),
    mobile: yup.string().when('registerType', {
        is: 'mobile',
        then: yup
            .string()
            .required('Please enter your mobile number')
            .test('isValidMobile', 'Please enter a valid mobile number', (value) => {
                if (value) {
                    return numberRegex.test(value) && value.length === 10;
                }
                return true;
            }),
    }),
    registerType: yup.string().required(),
});

const InitialStateSchema = yup.object({
    userId: yup
        .string()
        .required('Please enter PAN/PEKRN')
        .test('isValidPAN', 'Please enter a valid PAN/PEKRN', (value) =>
            panRegex.test(value || ''),
        ),
});

const ExistingUserSchema = yup.object({
    userId: yup
        .string()
        .required('Please enter PAN/PEKRN')
        .test('isValidPAN', 'Please enter a valid PAN/PEKRN', (value) =>
            panRegex.test(value || ''),
        ),
});

const InactiveValidationSchema = (userState: UserState) => {
    switch (userState) {
        case UserState.Initial:
            return InitialStateSchema;
        case UserState.Unregistered:
            return NewUserValidationSchema;
        case UserState.Registered:
            return ExistingUserSchema;
    }
};

export function InactiveFolios() {
    const [userState, userDispatch] = useReducer(userReducer, UserState.Initial);
    const [otpInput, setOtp] = useState<any>(new Array(6).fill(''));
    const [{ reqId, otpRef }, setOtpInfo] = useState(initState);
    const [registered, setRegistered] = useState<'Y' | 'N' | null>(null);
    const [registerDetails, setRegisterDetails] =
        useState<{ by: string; details: string } | null>(null);
    const [loading, setLoading] = useState(false);
    const [showDialog, setShowDialog] = useState(false);
    const [showPopup, setShowPopup] = useState(false);

    const history = useHistory();
    const reduxDispatch = useDispatch();

    const formik = useFormik<InactiveFolioLoginFormik>({
        initialValues,
        onSubmit: async (values) => {
            try {
                setLoading(true);
                switch (userState) {
                    case UserState.Initial:
                        const dormantPanCheckReqBody = {
                            pan: values.userId,
                            pekrn: '',
                            reqId: '',
                        };
                        const { body: panCheckResponse } = await dormantPanCheck(
                            dormantPanCheckReqBody,
                        )(reduxDispatch);
                        userDispatch({
                            action: UserEvent.CheckPan,
                            loginPanExists: panCheckResponse.loginPanExists,
                        });
                        setRegistered(panCheckResponse.loginPanExists);
                        setRegisterDetails({
                            by: panCheckResponse.contactDetailsMode,
                            details: panCheckResponse.contactDetails,
                        });
                        setShowPopup(true);
                        setTimeout(() => setShowPopup(false), 1000);
                        setOtpInfo((prevState: any) => ({
                            ...prevState,
                            otpRef: panCheckResponse.otpRef,
                            reqId: panCheckResponse.reqId,
                        }));
                        break;
                    case UserState.Unregistered:
                        const dormantRegisterReqBody = {
                            pan: values.userId,
                            reqId: '',
                            pekrn: '',
                            mobile: values.registerType === 'mobile' ? values.mobile : '',
                            email: values.registerType === 'email' ? values.email : '',
                        };
                        const { body: dormantRegisterResponse } = await registerDormantOtp(
                            dormantRegisterReqBody,
                        )(reduxDispatch);
                        setRegisterDetails({
                            by: values.registerType.toUpperCase(),
                            details:
                                values.registerType === 'mobile' ? values.mobile : values.email,
                        });
                        setOtpInfo((prevState: any) => ({
                            ...prevState,
                            otpRef: dormantRegisterResponse.otpRef,
                            reqId: dormantRegisterResponse.reqId,
                        }));
                        userDispatch({
                            action: UserEvent.RegisterUser,
                        });
                        break;
                    case UserState.Registered:
                        const dormantVerifyOtpReqBody = {
                            userId: values.userId,
                            reqId,
                            otpRef,
                            enteredOtp: otpInput.reduce(
                                (prev: any, curr: any) => `${prev}${curr}`,
                                '',
                            ),
                            action: 'DORMANT_PAN_CHECK',
                        };
                        const { body: verifyDormantResponse } = await verifyDormantOtp(
                            dormantVerifyOtpReqBody,
                        )(reduxDispatch);
                        if (verifyDormantResponse.reqId) {
                            history.push(`/links${InactiveFolioPaths.SEARCH}`, {
                                reqId: verifyDormantResponse.reqId,
                                attemptsLeft: verifyDormantResponse.attemptsLeft,
                                registerDetails,
                                foliosFound: false,
                                pan: values.userId,
                            });
                        }
                }
            } catch (err: any) {
                alert(err.message);
            } finally {
                setLoading(false);
            }
        },
        validationSchema: InactiveValidationSchema(userState),
        initialStatus: false,
        validateOnMount: false,
        // validate,
    });

    const reset = () => {
        setRegistered(null);
        setOtp(new Array(6).fill(''));
        formik.resetForm();
    };

    return (
        <div className="curved-bg">
            <h2 className="heading">
                <a onClick={() => history.goBack()}>
                    <img src="/images/left-arrow-black.svg" alt="icon" />
                </a>
                {dashboard.inactiveFolios.heading}
            </h2>
            <div className="curved-bg-content curved-bg-table-outer transmission-request card-bg-image">
                <div className="curved-bg-content-table">
                    <div>
                        {registered === IsAllow.No && (
                            <p
                                style={{
                                    fontSize: 14,
                                    color: '#836fe0',
                                    // padding: '10px',
                                    // marginTop: '-15px',
                                    marginBottom: '10px',
                                }}
                            >
                                {' '}
                                Looks like you are not registered with MFCentral, please provide
                                your details to proceed
                            </p>
                        )}
                        <Grid container>
                            <Grid item xs={12} md={4}>
                                <label
                                    style={{
                                        fontWeight: 500,
                                        fontSize: '15px',
                                        display: 'inline-block',
                                        marginBottom: '15px',
                                        marginLeft: '5px',
                                    }}
                                >
                                    Enter your PAN/PEKRN
                                </label>
                                <TextInput
                                    name="userId"
                                    label="PAN/PEKRN"
                                    className="filled-input-inactive"
                                    variant="filled"
                                    error={!!formik.errors.userId}
                                    value={formik.values.userId}
                                    helperText={formik.errors.userId}
                                    onChange={(e) => {
                                        formik.setFieldValue(
                                            'userId',
                                            e.target.value.toUpperCase(),
                                        );
                                    }}
                                    disabled={
                                        formik.isSubmitting || userState !== UserState.Initial
                                    }
                                    fullWidth
                                />
                            </Grid>
                        </Grid>

                        {registered === IsAllow.YES && (
                            <p
                                style={{
                                    fontSize: 14,
                                    color: '#836fe0',
                                    padding: '10px',
                                    marginTop: '-15px',
                                    marginBottom: '10px',
                                }}
                            >
                                {' '}
                                PAN/PEKRN is registered with MF Central
                            </p>
                        )}
                        {userState === UserState.Registered && (
                            <>
                                <OtpField
                                    value={otpInput}
                                    updateValue={(otp: string[]) => {
                                        setOtp(otp);
                                    }}
                                />
                                <p
                                    style={{
                                        fontSize: 14,
                                        color: '#836fe0',
                                        padding: '10px',
                                        marginTop: '-25px',
                                        marginBottom: '20px',
                                    }}
                                >
                                    OTP has been sent to{' '}
                                    {registered === IsAllow.YES
                                        ? registerDetails?.by === 'MOBILE'
                                            ? maskMobileNumber(registerDetails?.details)
                                            : maskEmail(registerDetails?.details || '')
                                        : formik.values.registerType === 'email'
                                        ? maskEmail(formik.values.email)
                                        : maskMobileNumber(formik.values.mobile)}
                                </p>
                                <DifferentPAN userDispatch={userDispatch} reset={reset} />
                            </>
                        )}
                        {userState === UserState.Unregistered && (
                            <>
                                <Grid container>
                                    <Grid item xs={12} md={4}>
                                        <ToggleTabs
                                            extraClass="additional-toggle"
                                            value={
                                                formik.values.registerType === 'email' ? OFF : ON
                                            }
                                            updateValue={(e: any) => {
                                                formik.setFieldValue(
                                                    'registerType',
                                                    e.target.checked ? 'mobile' : 'email',
                                                );
                                            }}
                                            offText={'Email'}
                                            onText={'Mobile'}
                                        />
                                        <TextInput
                                            style={{
                                                border: '1px solid black',
                                                borderRadius: '5px',
                                            }}
                                            name={
                                                formik.values.registerType === 'email'
                                                    ? 'email'
                                                    : 'mobile'
                                            }
                                            label={
                                                formik.values.registerType === 'email'
                                                    ? 'Enter Your email'
                                                    : 'Enter your mobile'
                                            }
                                            value={
                                                formik.values.registerType === 'email'
                                                    ? formik.values.email
                                                    : formik.values.mobile
                                            }
                                            error={
                                                formik.values.registerType === 'email'
                                                    ? !!formik.errors.email
                                                    : !!formik.errors.mobile
                                            }
                                            helperText={
                                                formik.values.registerType === 'email'
                                                    ? formik.errors.email
                                                    : formik.errors.mobile
                                            }
                                            onChange={(e) => {
                                                if (formik.values.registerType === 'email') {
                                                    formik.setFieldValue('email', e.target.value);
                                                } else {
                                                    formik.setFieldValue('mobile', e.target.value);
                                                }
                                            }}
                                            disabled={formik.isSubmitting}
                                            fullWidth
                                        />
                                    </Grid>
                                </Grid>
                                <Grid container>
                                    <Grid item xs={12} md={4}>
                                        <DifferentPAN userDispatch={userDispatch} reset={reset} />
                                    </Grid>
                                </Grid>
                            </>
                        )}
                        <p
                            className="if-terms-text"
                            style={{ fontSize: 14, fontFamily: 'Montserrat-Regular' }}
                        >
                            {form.termstext}{' '}
                            <a
                                onClick={() => {
                                    setShowDialog(true);
                                }}
                            >
                                {form.termsLink}
                            </a>
                            .
                        </p>
                        <SubmitButton
                            type="submit"
                            label="Proceed"
                            onClick={() => formik.submitForm()}
                            className="submit-button"
                            style={{
                                width: '30%',
                                marginLeft: 'auto',
                                marginRight: 'auto',
                                marginTop: '10%',
                            }}
                            id="submit-id"
                            disabled={!formik.isValid || loading}
                        />
                    </div>
                    <Dialog maxWidth={'sm'} open={showDialog}>
                        <div className="note-modals" style={{ maxWidth: '700px' }}>
                            <h3 style={{ alignSelf: 'center', textDecoration: 'underline' }}>
                                Terms and Conditions
                            </h3>
                            <p>
                                I am aware that the usage of the MFCentral platform is solely for
                                the purpose of identifying investments in the name of self or as a
                                legal heir or nominee or immediate relative. I am also aware that
                                the identification of investment is done based matching inputs
                                entered in the platform and information available with AMC/RTA at
                                the time of making investment on a best effort basisI am also aware
                                that the identification of investment is done on a best effort
                                basis.
                            </p>
                            <p>
                                In case of further scrutiny if it is found that the investment does
                                not pertain to the entity for which search was made, I will not hold
                                AMCs and RTAs CAMS and KFin Technologies Limited (“KFintech”) liable
                                for any loss/interest/claim.
                            </p>
                            <p>
                                I hereby undertake to provide all the required original documents
                                for verification upon demand by the AMC/RTAs to establish the
                                authenticity of the investor for which the search is made. I agree
                                to the Terms and conditions (link may be provided to the MF Central
                                T&C).
                            </p>
                            <p>
                                I confirm that the information provided during the query process is
                                accurate and complete. Furthermore, I will not hold AMCs and RTAs
                                CAMS and KFintech responsible for any liabilities arising out of
                                this information
                            </p>
                            <p>
                                I am responsible for maintaining the confidentiality of any
                                information accessed through this module and will ensure that it is
                                not shared with  any unauthorized parties.
                            </p>
                            <div className="note-modals-btns">
                                <Button
                                    className="customBtn"
                                    onClick={() => {
                                        setShowDialog(false);
                                    }}
                                >
                                    Accept
                                </Button>
                            </div>
                        </div>
                    </Dialog>
                </div>
            </div>
            {showPopup && (
                <Dialog maxWidth={'sm'} open={showPopup} aria-labelledby="enable-fingerprint-modal">
                    <div className="enable-fingerprint-modal">
                        <CircularProgress />
                        <p style={{ marginBottom: '0', marginTop: '12px' }}>
                            {'You are connecting to MITRA through the MF Central Platform.'}
                        </p>
                    </div>
                </Dialog>
            )}
        </div>
    );
}
